/**
 * WordPress dependencies
 */
import { MenuGroup, MenuItem } from '@safe-wordpress/components';
import { useSelect } from '@safe-wordpress/data';

/**
 * External dependencies
 */
import { store as NAB_SEGMENTS } from '@nab/segmentation-rules';
import { EMPTY_ARRAY } from '@nab/utils';
import type {
	SegmentationRule,
	SegmentationRuleCategory,
	SegmentationRuleCategoryName,
	SegmentationRuleTypeName,
} from '@nab/types';

export type SegmentationRuleTypeCategoryProps = {
	readonly category: SegmentationRuleCategory;
	readonly segmentationRules: ReadonlyArray< SegmentationRule >;
	readonly createSegmentationRule: ( type: SegmentationRuleTypeName ) => void;
	readonly onClose: () => void;
};

export const SegmentationRuleTypeCategory = ( {
	category,
	segmentationRules,
	createSegmentationRule,
	onClose,
}: SegmentationRuleTypeCategoryProps ): JSX.Element | null => {
	const segmentationRuleTypes = useSegmentationRuleTypes( category.name );

	if ( ! segmentationRuleTypes.length ) {
		return null;
	}

	return (
		<MenuGroup label={ category.title }>
			{ segmentationRuleTypes.map(
				( { name, title, icon: Icon, singleton } ) => (
					<MenuItem
						key={ name }
						role="menuitem"
						icon={ <Icon /> }
						{ ...{ iconPosition: 'left' } }
						onClick={ () => {
							createSegmentationRule( name );
							onClose();
						} }
						disabled={
							singleton &&
							segmentationRules.some(
								( rule ) => rule.type === name
							)
						}
					>
						{ title }
					</MenuItem>
				)
			) }
		</MenuGroup>
	);
};

// =====
// HOOKS
// =====

const useSegmentationRuleTypes = ( catName: SegmentationRuleCategoryName ) => {
	const ruleTypes = useSelect(
		( select ) =>
			select( NAB_SEGMENTS ).getSegmentationRuleTypes() || EMPTY_ARRAY,
		[]
	);
	return ruleTypes.filter( ( { category } ) => category === catName );
};
